import { useState, useEffect } from "react";
import { useRouter } from "next/router";
import useNextLesson from "@/helpers/jw-helpers/next-lesson";
import updateUserActivity from "@/utils/update-user-activity";
import DynamicStickyHeader from "../learning-pathways/dynamic-sticky-header";
import DetailsTab from "../learning-pathways/details-tabs";
import {
  useSetLpUid,
  useShowAlertNudge,
  useSurveyStore,
} from "@/context/store";
import useAuth from "@/utils/hooks/login-auth";
import { convertMinutesToHoursAndMinutes } from "@/utils/convert-to-hours-and-minutes";
import { LearningPathDurationIcon } from "../svgcomponents/lp-common-icons";
import Image from "next/image";
import CustomPopover from "../modal/custom-popover";
import { useQueryClient } from "@tanstack/react-query";

const TextLessonPage = ({
  content,
  videoConfig,
  close,
  setClose,
  isLearningPath,
  handleFeedbackSubmit,
  hasCompleted,
  courseProgress,
  handleStartCourse,
  getLessons,
  data,
  isLast,
  inCompletedLessons,
  incrementAttempts,
  isStandaloneCourse,
  videoEntry,
  startCourse,
  isResponsive,
  duration,
  nextLessonData,
}) => {
  const [remainingLessonsNudge, setRemainingLessonsNudge] = useState(false);
  const [hasCompletedLastLesson, setHasCompletedLastLesson] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const { uid } = useSetLpUid();
  // const courses = useCoursesStore((state) => state.courses);
  const { isAuthenticated } = useAuth();
  const { loggedInUser } = useAuth();
  const router = useRouter();
  const queryClient = useQueryClient();
  const isLesson = router?.query?.lessons;
  const courseUrl = router?.query?.course;
  const isCurrentLessonCompleted = courseProgress?.progress?.isCompleted;
  let pathname = router.asPath.replace(/\?.*$/, "");
  const nextLesson = useNextLesson(videoConfig, router, true);
  const { showAlertNudge, setShowAlertNudge } = useShowAlertNudge();
  const [term, setTerm] = useState(null);
  const total_lessons = data?.chapters[0]?.lessons?.lessons.length;
  const isLastQuiz =
    data?.chapters[0]?.lessons?.lessons[total_lessons - 1]?.is_quiz;
  const { showSurvey, setShowSurvey } = useSurveyStore();
  // First useEffect for image handling
  // Second useEffect for alert nudge
  useEffect(() => {
    if (showAlertNudge === "true") {
      setShowSurvey(true);
      setShowAlertNudge(true);
    }
  }, [showAlertNudge, setShowAlertNudge, showSurvey, setShowSurvey]);

  const handleReload = (res) => {
    if (!!res) {
      getLessons();
      setShowSurvey(true);
      setShowAlertNudge(true);
    }
  };

  const cancelNudge = () => {
    setRemainingLessonsNudge(false);
    queryClient.invalidateQueries({
      queryKey: ["courseLessonProgress", data?.uid],
    });
    queryClient.invalidateQueries({
      queryKey: ["learningPathLessons", uid],
    });
  };

  const handleNavigation = async () => {
    try {
      setIsLoading(true);

      // Handle quiz navigation first

      // Handle non-authenticated users
      if (!isAuthenticated) {
        incrementAttempts();
        nextLesson();
        return;
      }

      if (loggedInUser?.uid) {
        if (inCompletedLessons?.length === 1) {
          if (hasCompletedLastLesson) {
            if (isLearningPath) {
              window.location.href = `/academy/learning-paths/${isLearningPath}/${courseUrl}${nextLessonData?.url}`;
            } else {
              window.location.href = `/academy/courses/${courseUrl}${nextLessonData?.url}`;
            }
          }

          try {
            setIsLoading(true);
            const userData = {
              courseEntryUid: data?.uid,
              lessonEntryUid: videoEntry?.uid,
              courseActivity: {
                progress: {
                  percentage: 100,
                  isCompleted: true,
                },
              },
              lessonActivity: {
                progress: {
                  percentage: 100,
                  isCompleted: true,
                },
              },
            };

            updateUserActivity(userData, isStandaloneCourse)
              .then((res) => {
                if (res.status === 200) {
                  handleReload(
                    res?.data?.courseProgressTracking.find(
                      (course) =>
                        course?.courseEntryUid === userData?.courseEntryUid
                    )?.isNewlyCompleted
                  );
                  // setShowAlertNudge(true);
                  setHasCompletedLastLesson(true);
                  setIsLoading(false);
                }
              })
              .catch(() => {
                setIsLoading(false);
              });
          } catch (error) {
            setIsLoading(false);
          }
        } else {
          const userData = {
            courseEntryUid: data?.uid,
            lessonEntryUid: videoEntry?.uid,
            courseActivity: {
              progress: {
                percentage: 100,
                isCompleted: true,
              },
            },
            lessonActivity: {
              progress: {
                percentage: 100,
                isCompleted: true,
              },
            },
          };

          try {
            const res = await updateUserActivity(userData, isStandaloneCourse);

            if (res.status === 200) {
              // Check for incomplete lessons first
              if (inCompletedLessons?.length > 1 && isLast) {
                const pathSegments = pathname.split("/");
                pathSegments[pathSegments.length - 1] =
                  inCompletedLessons[0].url;
                const newPath = pathSegments.join("/");
                router.push(newPath);
              } else {
                // Increment attempts only once when needed
                if (
                  isLast ||
                  (inCompletedLessons?.length === 1 &&
                    !isCurrentLessonCompleted)
                ) {
                  handleReload(
                    res?.data?.courseProgressTracking.find(
                      (course) =>
                        course?.courseEntryUid === userData?.courseEntryUid
                    )?.isNewlyCompleted
                  );
                } else {
                  nextLesson();
                }
              }
            }
          } catch (error) {
            setIsLoading(false);
          }
        }
      }
    } catch (error) {
      setIsLoading(false);
    } finally {
      setIsLoading(false);
    }
  };
  const handleQuizNavigation = () => {
    if (isLearningPath) {
      window.location.href = `/academy/learning-paths/${isLearningPath}/${courseUrl}${nextLessonData?.url}`;
    } else {
      window.location.href = `/academy/courses/${courseUrl}${nextLessonData?.url}`;
    }
  };

  const handleCourseSubmit = () => {
    try {
      setIsLoading(true);
      const userData = {
        courseEntryUid: data?.uid,
        lessonEntryUid: videoEntry?.uid,
        courseActivity: {
          progress: {
            percentage: 100,
            isCompleted: true,
          },
        },
        lessonActivity: {
          progress: {
            percentage: 100,
            isCompleted: true,
          },
        },
      };

      updateUserActivity(userData, isStandaloneCourse)
        .then((res) => {
          if (res.status === 200) {
            handleReload(
              res?.data?.courseProgressTracking.find(
                (course) => course?.courseEntryUid === userData?.courseEntryUid
              )?.isNewlyCompleted
            );
            if (inCompletedLessons?.length > 1) {
              setRemainingLessonsNudge(true);
            }
            setIsLoading(false);
          }
        })
        .catch(() => {
          setIsLoading(false);
        });
    } catch (error) {
      setIsLoading(false);
    }
    // setRemainingLessonsNudge(true);
  };

  const handleNextIncompleteLesson = () => {
    if (isLastQuiz && isLast) {
      if (isLearningPath) {
        window.location.href = `/academy/learning-paths/${isLearningPath}/${courseUrl}${inCompletedLessons[0]?.url}`;
      } else {
        window.location.href = `/academy/courses/${courseUrl}${inCompletedLessons[0]?.url}`;
      }
    } else if (!isLastQuiz && isLast) {
      if (isLearningPath) {
        window.location.href = `/academy/learning-paths/${isLearningPath}/${courseUrl}${nextLessonData?.url}`;
      } else {
        window.location.href = `/academy/courses/${courseUrl}${nextLessonData?.url}`;
      }
    }
  };

  const handleKeyActivate = (e, handler) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      handler(e);
    }
  };
  return (
    <
      // style={{ padding: "0px 40px" }}
    >
      <div
        className="learning-path__container pb-4 "
        style={{ position: "relative" }}
      >
        <DynamicStickyHeader
          // learningPathwayDetails={data}
          isLearningPath={isLearningPath}
          courseDetails={videoEntry}
          duration={duration}
          term={term}
          created_at={videoEntry?.publish_details?.time}
          userData={courseProgress}
          isCourse={true}
          isLesson={isLesson}
          handleStartCourse={handleStartCourse}
          isTextLesson={true}
          nextLessonFunction={
            nextLessonData?.is_quiz && inCompletedLessons?.length >= 1
              ? handleNavigation
              : inCompletedLessons?.length >= 1
                ? handleNavigation
                : handleQuizNavigation
          }
          nextSubmitFunction={handleCourseSubmit}
          lessonLoading={isLoading}
          isLast={isLast}
          isLastQuiz={isLastQuiz}
          close={close}
          setClose={setClose}
          // lessonUid={videoEntry?.uid}
        />
        <DetailsTab
          data={data}
          overview={data.overview}
          // description={data.description}
          handleFeedbackSubmit={handleFeedbackSubmit}
          resources={data.right_rail.download_cta}
          faqs={data.faqs}
          isTextLesson={true}
          isResponsive={isResponsive}
          isStandaloneCourse={isStandaloneCourse}
          startCourse={startCourse}
          videoEntry={videoEntry}
          content={content}
          isLesson={isLesson}
          close={close}
        />

        {(isLast && !hasCompleted && isLastQuiz && isAuthenticated) ||
        (isLast && !hasCompleted && isAuthenticated) ? (
          inCompletedLessons?.length === 0 ? (
            <div className="w-100 d-flex justify-content-end">
              <button
                type="button"
                className="btn admy-primary-btn outline-focussed"
                style={{ maxWidth: "240px" }}
                disabled={isLoading}
                onClick={() => {
                  // setShowSurvey(true);
                  handleCourseSubmit();
                }}
                aria-label="Finish Course"
                tabIndex={0}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    handleCourseSubmit();
                  }
                }}
              >
                {isLoading
                  ? "Loading..."
                  : inCompletedLessons?.length === 0
                    ? "Course Completed"
                    : "Finish Course"}
              </button>{" "}
            </div>
          ) : (
            <div className=" curriculum-list-card__expanded-complete-div w-100">
              <span>
                Great job! You’ve completed the course! Click ‘Finish Course’ to
                complete your learning.
              </span>
              <button
                type="button"
                className="btn admy-primary-btn outline-focussed"
                style={{ maxWidth: "240px" }}
                disabled={isLoading}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    handleCourseSubmit();
                  }
                }}
                onClick={() => {
                  // setShowSurvey(true);
                  handleCourseSubmit();
                }}
              >
                {isLoading
                  ? "Loading..."
                  : inCompletedLessons?.length === 0
                    ? "Course Completed"
                    : "Finish Course"}
              </button>
            </div>
          )
        ) : (isLast && hasCompleted && !isLastQuiz) ||
          (videoEntry.text_lesson && isStandaloneCourse) ||
          (isLast && hasCompleted && isLastQuiz) ||
          (!isAuthenticated && isLast && !isLastQuiz) ? null : (
          <div className="d-flex w-100 flex-column cs-gap-12">
            <h3 className="curriculum-list-card__expanded-main-title p-0 m-0">
              Next Up
            </h3>
            <div
              className={`d-flex flex-column outline-focussed ${close ? "flex-sm-row " : "flex-lg-row align-items-center justify-content-center"} certificate-card mw-1920 justify-content-between  border rounded-3 border-secondary-subtle m-0 h-fit gap-0 align-items-center`}
              aria-label="Next lesson"
              tabIndex={-1}
              onClick={handleNavigation}
              onKeyDown={(e) => handleKeyActivate(e, handleNavigation)}
            >
              {/* next lesson button */}
              <div className="d-flex flex-column flex-sm-row gap-2 pb-3 pb-sm-0">
                {nextLessonData?.lesson_thumbnail?.url && (
                  <div className="text-center curriculum-list-card__expanded-image">
                    <div
                      style={{
                        width: "200px",
                        height: "122px",
                        position: "relative",
                      }}
                    >
                      <Image
                        src={nextLessonData?.lesson_thumbnail?.url}
                        alt="course-image"
                        fill
                        style={{
                          objectFit: "contain",
                        }}
                        aria-hidden="true"
                      />
                    </div>
                  </div>
                )}
                <div className="d-flex flex-column justify-content-center align-items-center align-items-sm-start gap-2 m-0 p-0">
                  <p className="curriculum-list-card__expanded-label">
                    {nextLessonData?.is_quiz
                      ? "Quiz"
                      : nextLessonData?.text_lesson?.length > 0
                        ? "Article"
                        : "Video"}
                  </p>
                  <h3
                    aria-label={nextLessonData?.title}
                    className="curriculum-list-card__expanded-title-next p-0 m-0"
                  >
                    {/* {cardData?.title || title} */}
                    {nextLessonData?.title}
                  </h3>
                  <p
                    aria-label={`Completed on `}
                    className="curriculum-list-card__complete-closed-date"
                  >
                    {!nextLessonData?.is_quiz && <LearningPathDurationIcon />}
                    {!nextLessonData?.is_quiz &&
                      (nextLessonData?.time_to_read ||
                        (Array.isArray(nextLessonData?.video_lesson) &&
                        nextLessonData?.video_lesson.length > 0
                          ? convertMinutesToHoursAndMinutes(
                              nextLessonData?.video_lesson[0]?.duration,
                              typeof nextLessonData?.video_lesson[0]?.duration
                            )
                          : ""))}
                  </p>
                </div>
              </div>
              <button
                onClick={handleNavigation}
                className="admy-primary-btn-outlined outline-focussed"
                type="button"
                aria-label="Move To Next Lesson"
                tabIndex={0}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    handleNavigation();
                  }
                }}
                // disabled={isLoading}
              >
                {isLoading ? "Please Wait ..." : "Move To Next Lesson"}
                {/* {!isLoading && <RigthArrowIcon />} */}
              </button>
            </div>
          </div>
        )}
      </div>
      <CustomPopover
        title="remaining lesson"
        cancelTitle="go back"
        isModalOpen={remainingLessonsNudge}
        uid="bltac339725992076a7"
        customCancelHandler={cancelNudge}
        onSubmit={() => handleNextIncompleteLesson()}
        // onSubmit={() => handleNavigation()}
        submitTitle="go to next lesson"
        message="You need to log in to access this content and track your progress."
        delay={0}
      />
    </>
  );
};

export default TextLessonPage;
